import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { RoomDao } from "@/data/dao/service/RoomDao";
import { OfferScheduleType } from "@/data/model/Enums";
import type { IOfferSchedule } from "@/data/model/OfferSchedule";
import type { IRoom, IRoomServiceOrderUseCase } from "@/data/model/Room";
import { Format } from "@/data/util/Format";

import { translate } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { portalEvents } from "@/portal/events/portalEvents";
import { OfferScheduleWindow } from "@/portal/modal/OfferScheduleWindow";
import { PortalFormat } from "@/portal/util/PortalFormat";

export class OfferScheduleTableController {
    private $element: JQuery;
    private dao: CatalogDao;
    private dataTable: DataTable<IOfferSchedule>;
    private search: TableFilter;

    private scheduleWindow?: OfferScheduleWindow;

    constructor(element: JQuery) {
        this.$element = element;

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        const tableConfig: IDataTableConfig<IOfferSchedule> = {
            columnMapping: [
                {
                    name: "",
                    property: "scheduleType",
                    styles: { width: "5%" },
                    formatter: (m, p) => {
                        switch (p) {
                            case OfferScheduleType.Standard:
                                return "";
                            case OfferScheduleType.DifferentOffer:
                                return '<div class="typeicon warning"><span class="la la-warning"></span></div>';
                            case OfferScheduleType.Closed:
                                return '<div class="typeicon danger"><span class="la la-ban"></span></div>';
                        }
                    },
                },
                {
                    name: i18n("general.entities.offer"),
                    property: "offer",
                    styles: { width: "11%" },
                    formatter: (m, p) =>
                        p === undefined
                            ? i18n("offerSchedule.allOffers")
                            : $(`<a href="/offer/${m.offer!.id}">${Format.htmlEscape(p.name)}</a>`).click((e) =>
                                  e.stopPropagation()
                              ),
                },
                {
                    name: i18n("general.entities.rooms"),
                    property: "rooms",
                    styles: { width: "14%" },
                    formatter: (m, p) =>
                        p === undefined || p.length === 0
                            ? i18n("offerSchedule.allRooms")
                            : PortalFormat.toCommaSeparatedList<IRoom>(
                                  p,
                                  (c) => Format.htmlEscape(c.name)!,
                                  (c) => `/room/${c.id}`,
                                  4
                              ),
                },
                {
                    name: i18n("general.entities.orderApplications"),
                    property: "orderUseCases",
                    styles: { width: "14%" },
                    formatter: (m, p) =>
                        p === undefined || p.length === 0
                            ? i18n("offerSchedule.allOrderApplications")
                            : PortalFormat.toCommaSeparatedList<IRoomServiceOrderUseCase>(
                                  p,
                                  (c) => translate(c.name),
                                  (c) => `/order-application/${c.id}`,
                                  4
                              ),
                },
                {
                    name: i18n("general.fields.date"),
                    property: "from",
                    styles: { width: "12%" },
                    formatter: (m) => PortalFormat.timeRange(m.from, m.until),
                },
                {
                    name: i18n("offerSchedule.fields.weekdayAndTime"),
                    property: "shifts",
                    styles: { width: "20%" },
                    sortable: false,
                    formatter: (m) => PortalFormat.shiftTimes(m.shifts),
                },
                {
                    name: i18n("general.fields.internalNote"),
                    property: "internalNote",
                },
                {
                    name: "",
                    property: "id",
                    styles: { width: "110px", "text-align": "right" },
                    formatter: () => {
                        return $(
                            '<span><a class="btn btn-sm btn-clean btn-icon btn-icon-md"><i class="la la-edit"></i></a></span>'
                        );
                    },
                },
            ],
            filterProvider: {
                getFilter: () => this.search.getValue(),
                setFilter: (d) => this.search.setValue(d),
            },
            defaultSortAsc: false,
            locationDependend: true,
            useJsHistory: false,
            defaultSortProperty: undefined,
        };

        this.search = new TableFilter().appendTo(this.$element.find("> .kt-portlet__body.tablefilter"));
        this.search.addDropdownFilter(
            "offerId",
            Factory.getInstance<CatalogDao>(CatalogDao).offerDataSource,
            true,
            i18n("offerSchedule.allOffers"),
            undefined,
            undefined,
            "col-md-3"
        );
        this.search.addDropdownFilter(
            "roomId",
            Factory.getInstance<RoomDao>(RoomDao),
            true,
            i18n("offerSchedule.allRooms"),
            undefined,
            undefined,
            "col-md-2"
        );
        this.search.addDropdownFilterFromEnum(
            "scheduleType",
            OfferScheduleType,
            "enums.OfferScheduleType",
            i18n("offerSchedule.allTypes"),
            undefined,
            undefined,
            "col-md-3"
        );

        this.dataTable = new DataTable<IOfferSchedule>(
            this.$element.find("> .kt-portlet__body > .kt-datatable"),
            this.dao.offerScheduleDataSource,
            tableConfig
        );
        this.dataTable.onClick(
            (m) => {
                location.href = `/offerschedule/${m.id}`;
            },
            (m) => {
                window.open(`/offerschedule/${m.id}`);
            }
        );

        this.dataTable.listenTo(this.search);
        portalEvents.OnLocationChanged.add(async () => {
            await this.search.refreshDropdownFilter("offerId");
            await this.search.refreshDropdownFilter("roomId");
            this.dataTable.loadData(true);
        }, this);

        element.find("#previewbtn").click((e) => {
            e.preventDefault();
            this.showPlanningPreview();
        });
    }

    private showPlanningPreview() {
        if (this.scheduleWindow == undefined) {
            this.scheduleWindow = new OfferScheduleWindow();
        }

        this.scheduleWindow.show();
    }
}
